import * as BasketUpdated from '@ori-events/basket-updated';
import * as RequestShoppingContext from '@ori-events/request-shopping-context';
import { getLogger, LogLevel } from '@ori/checkout-utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { APP_NAME, BASKET_PROVIDER_POLL_TIMEOUT } from './constants';
import { shoppingContextActions } from './shoppingContextActions';

// TODO: This should be imported from @ori/shopping-bag-provider, but doesn't seem to be exported to the NPM server anymore?

const logDebug = getLogger(APP_NAME, LogLevel.Debug);
const logError = getLogger(APP_NAME, LogLevel.Error);
export const defaultActionEvents = [RequestShoppingContext.Name];
export const removeDuplicates = original => original.filter((value, index) => original.indexOf(value) === index);
export const getEvents = eventNames => {
  const failedEvents = [];
  const successEvents = [BasketUpdated.Name];
  const triggerEvents = removeDuplicates([...(eventNames ?? []), ...defaultActionEvents]);
  triggerEvents.forEach(value => {
    const {
      failedEvent,
      successEvent
    } = shoppingContextActions.get(value) ?? {};
    if (failedEvent) {
      failedEvents.push(failedEvent);
    }
    if (successEvent) {
      successEvents.push(successEvent);
    }
  });
  return {
    failedEvents: removeDuplicates(failedEvents),
    successEvents: removeDuplicates(successEvents),
    triggerEvents
  };
};
export const useEvents = eventNames => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [shoppingContext, setShoppingContext] = useState();
  const {
    failedEvents,
    successEvents,
    triggerEvents
  } = getEvents(eventNames);
  const triggerActionHandler = useCallback(event => {
    logDebug(`Loading shopping context was triggered via ${event.type} event.`);
    setIsLoading(true);
  }, []);
  const successActionHandler = useCallback(event => {
    logDebug('Shopping context was successfully loaded.\n\tshoppingContext', event.detail);
    setShoppingContext(event.detail);
    setIsLoading(false);
    setIsLoaded(true);
  }, []);
  const failedActionHandler = useCallback(event => {
    logError('Error loading shopping context', event.detail.message);
    setIsLoading(false);
    // TO-DO: Should the older shopping context be reset to undefined, or just keep using what was loaded before?
    // setIsLoaded(false);
    // setShoppingContext(undefined);
  }, []);
  useEffect(() => {
    triggerEvents.map(eventName => window.addEventListener(eventName, triggerActionHandler));
    successEvents.map(eventName => window.addEventListener(eventName, successActionHandler));
    failedEvents.map(eventName => window.addEventListener(eventName, failedActionHandler));
    return () => {
      triggerEvents.map(eventName => window.removeEventListener(eventName, triggerActionHandler));
      successEvents.map(eventName => window.removeEventListener(eventName, successActionHandler));
      failedEvents.map(eventName => window.removeEventListener(eventName, failedActionHandler));
    };
  }, [failedActionHandler, failedEvents, successActionHandler, successEvents, triggerActionHandler, triggerEvents]);
  useEffect(() => {
    // Starts shopping context loading. Should be called only once after the window.isBasketProviderReady gets resolved.
    const startContextLoading = () => {
      logDebug('Started loading shopping context data.');
      const event = new CustomEvent(RequestShoppingContext.Name);
      window.dispatchEvent(event);
    };

    // Waiting for basket provider promise to become available & resolved.
    let timeoutId;
    const poll = () => {
      if (window.isBasketProviderReady === undefined) {
        logDebug(`The isBasketProviderReady promise is not available yet. Waiting ${BASKET_PROVIDER_POLL_TIMEOUT} ms ...`);
        timeoutId = setTimeout(() => poll(), BASKET_PROVIDER_POLL_TIMEOUT);
      } else {
        logDebug('The isBasketProviderReady promise is available. Waiting for resolved state ...');
        void window.isBasketProviderReady.then(startContextLoading);
      }
    };
    poll();
    return () => {
      clearTimeout(timeoutId);
    };
  }, []);
  const contextValue = useMemo(() => ({
    isLoaded,
    isLoading,
    shoppingContext
  }), [isLoaded, isLoading, shoppingContext]);
  return contextValue;
};
//# sourceMappingURL=useEvents.js.map